import { useState } from 'react';
import type { ExpenseCostSplit } from '../../api';
import type { Aircraft } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import {
  formatIssuePerformerLabel,
  issuePerformerError,
  performerFromAircraft,
  type MaintenancePerformerType,
} from '../../utils/aircraftMaintenance';
import { ownerSplitError, ownerSplitRequest } from '../../utils/expenseSplit';
import { parseIssueCost } from '../../utils/issueDisplay';
import IssueResolveFields from '../IssueResolveFields/IssueResolveFields';
import ModalPortal from '../modal/ModalPortal';
import './ResolveIssueModal.css';

interface Props {
  aircraft?: Aircraft | null;
  onClose: () => void;
  onConfirm: (data: {
    resolved_date: string;
    resolved_by: string;
    cost: number;
    cost_split: ExpenseCostSplit;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
  }) => void | Promise<void>;
}

const getToday = () => new Date().toISOString().split('T')[0];

const ResolveIssueModal = ({ aircraft, onClose, onConfirm }: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const initial = performerFromAircraft(aircraft);
  const [resolvedDate, setResolvedDate] = useState(getToday);
  const [performerType, setPerformerType] =
    useState<MaintenancePerformerType>(initial.type);
  const [performerName, setPerformerName] = useState(initial.name);
  const [cost, setCost] = useState('0');
  const [costSplit, setCostSplit] = useState<ExpenseCostSplit>('shares');
  const [manualShares, setManualShares] = useState<Record<number, string>>({});

  const handleConfirm = () => {
    if (submitting) return;
    if (!resolvedDate) {
      showError('Укажите дату устранения');
      return;
    }
    const performerError = issuePerformerError(performerType, performerName);
    if (performerError) {
      showError(performerError);
      return;
    }
    const parsed = parseIssueCost(cost);
    if (!parsed.ok) {
      showError(parsed.error);
      return;
    }
    const split: ExpenseCostSplit = parsed.value > 0 ? costSplit : 'shares';
    const splitError = ownerSplitError(split, manualShares);
    if (splitError) {
      showError(splitError);
      return;
    }

    void runSubmit(() =>
      onConfirm({
        resolved_date: resolvedDate,
        resolved_by: formatIssuePerformerLabel(performerType, performerName),
        cost: parsed.value,
        ...ownerSplitRequest(split, manualShares),
      })
    );
  };

  return (
    <ModalPortal>
    <div className="modal-overlay" onClick={onClose}>
      {feedbackNode}
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Устранение неисправности</h3>
        <div className="modal-body">
          <label>Дата</label>
          <input
            type="date"
            value={resolvedDate}
            onChange={(e) => setResolvedDate(e.target.value)}
          />
          <IssueResolveFields
            resolvedDate={resolvedDate}
            performerType={performerType}
            performerName={performerName}
            cost={cost}
            costSplit={costSplit}
            manualShares={manualShares}
            onPerformerTypeChange={setPerformerType}
            onPerformerNameChange={setPerformerName}
            onCostChange={setCost}
            onCostSplitChange={setCostSplit}
            onManualSharesChange={setManualShares}
          />
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? '…' : 'Устранить'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default ResolveIssueModal;
